import {
  FileText,
  Import,
  Settings,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  AccountsIcon,
  ContactsIcon,
  LeadsIcon,
  PipelineIcon,
  TodayIcon,
  type NavIcon,
} from "./navIcons";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";

const Header = () => {
  const { title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/leads/*", location.pathname)) {
    currentPath = "/leads";
  } else if (matchPath("/settings/*", location.pathname)) {
    currentPath = "/settings";
  } else {
    currentPath = false;
  }

  return (
    <nav className="sticky top-0 flex h-screen w-[72px] shrink-0 flex-col gap-1 bg-sidebar px-2 py-3 text-sidebar-foreground">
      <Link
        to="/"
        title={title}
        className="mx-auto mb-3 mt-1 flex size-[34px] items-center justify-center rounded-lg border-2 border-sidebar-primary bg-black/40 text-lg font-bold lowercase text-sidebar-primary no-underline"
      >
        {title.charAt(0)}
      </Link>
      <NavigationTab
        label={translate("crm.today", { _: "Today" })}
        icon={TodayIcon}
        to="/"
        isActive={currentPath === "/"}
      />
      <NavigationTab
        label={translate("resources.leads.name", {
          smart_count: 2,
          _: "Leads",
        })}
        icon={LeadsIcon}
        to="/leads"
        isActive={currentPath === "/leads"}
      />
      <NavigationTab
        label={translate("crm.deals.pipeline", { _: "Pipeline" })}
        icon={PipelineIcon}
        to="/deals"
        isActive={currentPath === "/deals"}
      />
      <NavigationTab
        label={translate("resources.companies.name", { smart_count: 2 })}
        icon={AccountsIcon}
        to="/companies"
        isActive={currentPath === "/companies"}
      />
      {/* Modules not reworked for zCRM yet: still Atomic CRM's screens. */}
      <div
        role="separator"
        className="mx-2 my-2 border-t border-sidebar-foreground/20"
      />
      <NavigationTab
        label={translate("resources.contacts.name", { smart_count: 2 })}
        icon={ContactsIcon}
        to="/contacts"
        isActive={currentPath === "/contacts"}
        isWorkInProgress
      />
      <div className="mt-auto flex flex-col items-center gap-1">
        <CanAccess resource="configuration" action="edit">
          <NavigationTab
            label={translate("crm.settings.title")}
            icon={Settings}
            to="/settings"
            isActive={currentPath === "/settings"}
          />
        </CanAccess>
        <ThemeModeToggle />
        <RefreshButton />
        <UserMenu>
          <ProfileMenu />
          <CanAccess resource="sales" action="list">
            <UsersMenu />
          </CanAccess>
          <CanAccess resource="import" action="create">
            <ImportFromJsonMenuItem />
          </CanAccess>
          <CanAccess resource="changelog" action="list">
            <ChangelogMenuItem />
          </CanAccess>
        </UserMenu>
      </div>
    </nav>
  );
};

const NavigationTab = ({
  label,
  icon: Icon,
  to,
  isActive,
  isWorkInProgress = false,
}: {
  label: string;
  icon: LucideIcon | NavIcon;
  to: string;
  isActive: boolean;
  /** Greyed out: the module isn't reworked for zCRM yet. */
  isWorkInProgress?: boolean;
}) => (
  <Link
    to={to}
    aria-current={isActive ? "page" : undefined}
    title={isWorkInProgress ? `${label}: work in progress` : undefined}
    className={`flex w-full flex-col items-center gap-1 rounded-lg py-2.5 text-2xs leading-tight no-underline transition-colors ${
      isActive
        ? "bg-sidebar-primary font-semibold text-sidebar-primary-foreground"
        : isWorkInProgress
          ? "text-sidebar-foreground/40 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground/70"
          : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
    }`}
  >
    <Icon className="size-5" strokeWidth={1.8} />
    {label}
    {isWorkInProgress ? (
      <span className="sr-only">(work in progress)</span>
    ) : null}
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
